// Client/src/utils/stateLrs/aldot.js
// ALDOT (Alabama DOT) LRS adapter. Data source facts verified live 2026-07-16 —
// see docs/superpowers/specs/2026-07-16-lrs-milepost-route-creation-design.md
// and memory/reference_aldot_lrs_milepost_api.md.
//
// Layer 2 "Milepost"  — statewide milepost network (I-/US-/SR-), 1,610 sections.
// Layer 1 "Milepoint" — full network incl. county inventory routes, 162k sections;
//                        measures reset per county ("milepoint", not "milepost").

import {
  cardinalFromSections,
  measureRangesFromSections,
  interpolatePointAtMeasure,
  projectPointToMeasure,
} from './lrsMath';
import { AL_COUNTY_NAMES, AL_COUNTY_FIPS_BY_NAME, AL_COUNTY_REGION } from './alCounties';

export const ROUTE_TYPE_LABEL = { IN: 'I', US: 'US', AL: 'SR', IV: 'IV', NC: 'NC', RP: 'Ramp' };

// ALDOT's RouteDirection coded-value domain (field dDirection): 0 = the
// inventory (primary, fully-measured) centerline — almost always the one you
// want; 1 = non-inventory (opposite carriageway / secondary, often only a
// fragment). NOT a compass direction — statewide routes derive NB/SB/EB/WB
// from geometry; this is the human-readable fallback/disambiguation label.
export const ROUTE_DIRECTION_LABEL = { 0: 'Inventory', 1: 'Non-Inventory' };

export function directionLabel(routeDirection) {
  return ROUTE_DIRECTION_LABEL[routeDirection] ?? `Dir ${routeDirection}`;
}

/** Plain-language explanation for a label that carries a direction term, so
 *  "Inventory" / "Non-Inventory" aren't opaque jargon in the picker. Returns
 *  null for cardinal-labeled (NB/SB/EB/WB) routes, which need no gloss. */
export function describeDirectionLabel(label) {
  if (typeof label !== 'string') return null;
  if (label.includes('Non-Inventory')) {
    return 'Opposite / secondary side of the road, often only partly mapped — pick the Inventory side unless you specifically need this one.';
  }
  if (label.includes('Inventory')) {
    return 'The road’s primary, fully-measured centerline — the usual choice.';
  }
  return null;
}

/** County FIPS → zone object (Alabama's measure-reset zone is the county). */
export function buildZone(countyCode) {
  if (!countyCode) return null;
  return {
    kind: 'county',
    code: countyCode,
    name: AL_COUNTY_NAMES[countyCode] ?? `County ${countyCode}`,
    region: AL_COUNTY_REGION[countyCode] ?? null,
  };
}

// ─── Normalization ────────────────────────────────────────────────────────────

/** ArcGIS query JSON → NormalizedSection[]. Handles both layer field spellings. */
export function normalizeArcgisSections(arcgisJson) {
  return (arcgisJson?.features ?? []).map((f) => {
    const a = f.attributes ?? {};
    return {
      routeName: a.RouteName ?? null,
      routeId: a.RouteId ?? a.RouteID ?? null,
      routeType: a.RouteType ?? null,
      routeNumber: Number(a.RouteNumber),
      routeDirection: Number(a.RouteDirection),
      countyCode: a.CountyCode ?? a.CountyFIPSCode ?? null,
      paths: f.geometry?.paths ?? [],
    };
  });
}

// ─── Statewide (milepost) grouping ────────────────────────────────────────────

function statewideKey(s) {
  return `milepost:${s.routeType}-${s.routeNumber}-${s.routeDirection}`;
}

export function groupStatewideSections(sections) {
  const groups = new Map();
  for (const s of sections ?? []) {
    const key = statewideKey(s);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const entries = [];
  for (const [key, group] of groups) {
    const { routeType, routeNumber, routeDirection } = group[0];
    const cardinal = cardinalFromSections(group);
    const typeLabel = ROUTE_TYPE_LABEL[routeType] ?? routeType;
    const dirLabel = cardinal ?? directionLabel(routeDirection);
    entries.push({
      key,
      network: 'milepost',
      label: `${typeLabel}-${routeNumber} ${dirLabel}`,
      routeType,
      routeNumber,
      routeDirection,
      countyCode: null,
      mRanges: measureRangesFromSections(group),
    });
  }
  entries.sort((a, b) =>
    a.routeType.localeCompare(b.routeType)
    || a.routeNumber - b.routeNumber
    || a.routeDirection - b.routeDirection);
  return entries;
}

// Below this, ALDOT data carries stub fragments (e.g. SR-50 dir 1 is a single
// 3-vertex, ~0.02-mile section at measure 523.07–523.09) that can't anchor a
// real route. 0.1 mi ≈ 528 ft — no drivable state-route corridor is shorter.
const DEGENERATE_MIN_SPAN_MILES = 0.1;

function rangesSpanMiles(mRanges) {
  return (mRanges ?? []).reduce((total, [lo, hi]) => total + (hi - lo), 0);
}

/** Post-process grouped statewide entries for the picker: drop degenerate stub
 *  routes (near-zero measure span — ALDOT data fragments) and disambiguate any
 *  entries left with identical labels (two directions whose geometry both
 *  resolve to the same cardinal) by appending the raw direction. Kept separate
 *  from groupStatewideSections so single-section labeling (locatePoint) is
 *  unaffected. */
export function finalizeStatewideEntries(entries) {
  const kept = (entries ?? []).filter(
    (e) => rangesSpanMiles(e.mRanges) >= DEGENERATE_MIN_SPAN_MILES,
  );
  const labelCounts = new Map();
  for (const e of kept) labelCounts.set(e.label, (labelCounts.get(e.label) ?? 0) + 1);
  return kept.map((e) =>
    labelCounts.get(e.label) > 1
      ? { ...e, label: `${e.label} (${directionLabel(e.routeDirection)})` }
      : e,
  );
}

// ─── Local (milepoint) entries ────────────────────────────────────────────────

export function localRouteEntryFromSection(s) {
  const typeLabel = ROUTE_TYPE_LABEL[s.routeType] ?? s.routeType;
  const county = AL_COUNTY_NAMES[s.countyCode] ?? `County ${s.countyCode}`;
  return {
    key: `milepoint:${s.routeType}-${s.routeNumber}-${s.routeDirection}-${s.countyCode}`,
    network: 'milepoint',
    label: `${typeLabel}-${s.routeNumber} (${county}) ${directionLabel(s.routeDirection)}`,
    routeType: s.routeType,
    routeNumber: s.routeNumber,
    routeDirection: s.routeDirection,
    countyCode: s.countyCode,
    zone: buildZone(s.countyCode),
    mRanges: null, // unknown until geometry is fetched
  };
}

export function buildLocalRouteEntries(arcgisJson) {
  return normalizeArcgisSections(arcgisJson).map(localRouteEntryFromSection);
}

// ─── Search WHERE-builder (contains semantics per spec) ───────────────────────

export function buildLocalWhere(term) {
  const cleaned = String(term ?? '').trim();
  if (!cleaned) return '1=1';

  // County names first — 'St. Clair' is multi-word, so match the whole term
  // (case/period-insensitive, word-bounded) before tokenizing the rest.
  let countyClause = null;
  let working = cleaned.toLowerCase().replace(/\./g, '');
  const countyEntries = Object.entries(AL_COUNTY_FIPS_BY_NAME)
    .map(([name, fips]) => [name.replace(/\./g, ''), fips])
    .sort((a, b) => b[0].length - a[0].length);
  for (const [normName, fips] of countyEntries) {
    const re = new RegExp(`\\b${normName}\\b`);
    if (re.test(working)) {
      countyClause = `CountyFIPSCode = '${fips}'`;
      working = working.replace(re, ' ');
      break; // one county filter per search
    }
  }

  const clauses = [];
  let consumedAll = true;
  const tokens = working.split(/[\s,-]+/).filter(Boolean);
  for (const token of tokens) {
    if (/^\d+$/.test(token)) {
      clauses.push(`RouteNumber = ${Number(token)}`);
    } else if (/^[a-z]{2}$/.test(token) && ROUTE_TYPE_LABEL[token.toUpperCase()]) {
      clauses.push(`RouteType = '${token.toUpperCase()}'`);
    } else {
      consumedAll = false;
    }
  }
  if (countyClause) clauses.push(countyClause);
  if (clauses.length > 0 && consumedAll) return clauses.join(' AND ');
  // Unrecognized content — contains match on the raw coded RouteID rather
  // than silently dropping what the user typed.
  const escaped = cleaned.toUpperCase().replace(/'/g, "''");
  return `UPPER(RouteID) LIKE '%${escaped}%'`;
}

// ─── Fetch layer / adapter ─────────────────────────────────────────────────────

const FEATURESERVER =
  'https://aldotegis.dot.state.al.us/egisrh/rest/services/Reference/EGISPro_Routes/FeatureServer';
const LAYER_MILEPOST = 2;
const LAYER_MILEPOINT = 1;
const SNAPSHOT_BASE = 'https://rsa-lrs-snapshots.s3.amazonaws.com/lrs/AL/v1';
// Until the S3 snapshot exists, every list/geometry fetch goes to live ALDOT
// full-precision — a large route on a slow moment can exceed a tight budget,
// surfacing a spurious "could not reach" timeout. 15s absorbs that; genuine
// outages still fail (just a few seconds later).
const FETCH_TIMEOUT_MS = 15000;
const LOCAL_SEARCH_LIMIT = 50;
// Server-side generalization for the bulk statewide list (meters, native SR is
// UTM 16N). Coarse geometry is only used for labels + measure ranges; precise
// per-route geometry is fetched on selection.
const COARSE_OFFSET_METERS = 500;

export class LrsUnavailableError extends Error {
  constructor(message, detail = null) {
    super(message);
    this.name = 'LrsUnavailableError';
    this.detail = detail;
  }
}

function buildQueryUrl(layerId, params) {
  const qs = new URLSearchParams({ f: 'json', ...params });
  return `${FEATURESERVER}/${layerId}/query?${qs.toString()}`;
}

// ALDOT's route layers are temporal: a plain query returns every time-slice,
// including retired geometry (~13% of layer 2). Restrict every query to the
// live network. (Verified 2026-07-20: 1610 total rows → 1404 current.)
const CURRENT_ONLY = 'ToDate IS NULL';

/** AND the current-network filter onto a caller's WHERE clause. */
function andCurrent(where) {
  return where && where !== '1=1' ? `(${where}) AND ${CURRENT_ONLY}` : CURRENT_ONLY;
}

/** Parse "milepost:IN-65-1" / "milepoint:IV-308-0-073" back into query parts. */
function parseRouteKey(key) {
  const [network, rest] = String(key).split(':');
  const parts = rest.split('-');
  return {
    network,
    routeType: parts[0],
    routeNumber: Number(parts[1]),
    routeDirection: Number(parts[2]),
    countyCode: parts[3] ?? null,
  };
}

const LOCATE_RADIUS_METERS = 150;

export function createAldotAdapter({ fetchImpl } = {}) {
  const doFetch = fetchImpl ?? ((...args) => fetch(...args));
  const geometryCache = new Map(); // key → { sections, mRanges }
  let statewideCache = null;       // RouteEntry[]
  let localIndexCache = null;      // RouteEntry[] — parsed local-index.json (fallback path)
  let sourceInfo = { statewideSource: null, snapshotDate: null };
  const locateCache = new Map();   // "lat,lng"@5dp → Promise<LrsPointLocation|null>

  async function fetchJson(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await doFetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new LrsUnavailableError(
          'The road data service is unavailable right now.',
          `HTTP ${res.status} from ${url}`,
        );
      }
      const body = await res.json();
      // ArcGIS reports failures as HTTP 200 with an error body.
      if (body && body.error) {
        throw new LrsUnavailableError(
          'The road data service returned an error.',
          `ArcGIS error ${body.error.code}: ${body.error.message}`,
        );
      }
      return body;
    } catch (err) {
      if (err instanceof LrsUnavailableError) throw err;
      throw new LrsUnavailableError(
        'Could not reach the road data service (network problem or timeout).',
        err?.message ?? null,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  /** Layer query with resultOffset pagination (ALDOT maxRecordCount is 100k,
   *  so statewide fits in one page — the loop is a safety net). */
  async function fetchAllFeatures(layerId, params) {
    const features = [];
    let offset = 0;
    for (;;) {
      const body = await fetchJson(buildQueryUrl(layerId, {
        ...params, resultOffset: String(offset),
      }));
      features.push(...(body.features ?? []));
      if (!body.exceededTransferLimit || !body.features?.length) break;
      offset += body.features.length;
    }
    return { features };
  }

  async function fetchStatewideSectionsLive() {
    const raw = await fetchAllFeatures(LAYER_MILEPOST, {
      where: CURRENT_ONLY,
      outFields: 'RouteId,RouteName,RouteType,RouteNumber,RouteDirection,CountyCode,RouteSection',
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
      maxAllowableOffset: String(COARSE_OFFSET_METERS),
    });
    return normalizeArcgisSections(raw);
  }

  async function fetchRouteSectionsLive(parsed) {
    const layerId = parsed.network === 'milepost' ? LAYER_MILEPOST : LAYER_MILEPOINT;
    const clauses = [
      `RouteType = '${parsed.routeType}'`,
      `RouteNumber = ${parsed.routeNumber}`,
      `RouteDirection = ${parsed.routeDirection}`,
    ];
    if (parsed.network === 'milepoint' && parsed.countyCode) {
      clauses.push(`CountyFIPSCode = '${parsed.countyCode}'`);
    }
    clauses.push(CURRENT_ONLY);
    const raw = await fetchAllFeatures(layerId, {
      where: clauses.join(' AND '),
      outFields: '*',
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    });
    return normalizeArcgisSections(raw);
  }

  async function getRouteGeometry(key) {
    if (geometryCache.has(key)) return geometryCache.get(key);
    const parsed = parseRouteKey(key);
    let sections = null;
    if (parsed.network === 'milepost') {
      try {
        const file = `${parsed.routeType}-${parsed.routeNumber}-${parsed.routeDirection}`;
        const snap = await fetchJson(`${SNAPSHOT_BASE}/routes/${file}.json`);
        if (Array.isArray(snap.sections) && snap.sections.length > 0) sections = snap.sections;
      } catch (_) { /* fall through to live */ }
    }
    if (!sections) {
      try {
        sections = await fetchRouteSectionsLive(parsed);
      } catch (err) {
        if (parsed.network === 'milepoint') {
          throw new LrsUnavailableError(
            'Resolving local-road milepoints requires a live ALDOT connection — currently unavailable.',
          );
        }
        throw err;
      }
    }
    const result = { sections, mRanges: measureRangesFromSections(sections) };
    geometryCache.set(key, result);
    return result;
  }

  async function querySectionsNear(layerId, point) {
    const body = await fetchJson(buildQueryUrl(layerId, {
      where: CURRENT_ONLY,
      geometry: JSON.stringify({ x: point.lng, y: point.lat, spatialReference: { wkid: 4326 } }),
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      distance: String(LOCATE_RADIUS_METERS),
      units: 'esriSRUnit_Meter',
      outFields: '*',
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    }));
    return normalizeArcgisSections(body);
  }

  function nearestOnSections(sections, point, network) {
    let best = null;
    for (const s of sections) {
      const proj = projectPointToMeasure([s], point);
      if (!proj || proj.distanceMeters > LOCATE_RADIUS_METERS) continue;
      if (best && proj.distanceMeters >= best.distanceMeters) continue;
      best = { section: s, m: proj.m, distanceMeters: proj.distanceMeters };
    }
    if (!best) return null;
    const s = best.section;
    const entry = network === 'milepost'
      ? groupStatewideSections([s])[0]
      : localRouteEntryFromSection(s);
    return {
      routeKey: entry.key,
      routeLabel: entry.label,
      network,
      measure: best.m,
      zone: buildZone(s.countyCode),
      distanceMeters: best.distanceMeters,
    };
  }

  return {
    stateCode: 'AL',
    stateName: 'Alabama (ALDOT)',
    capabilities: { speedLimits: false },

    getSourceInfo() {
      return { ...sourceInfo };
    },

    async listStatewideRoutes() {
      if (statewideCache) return statewideCache;
      let sections;
      try {
        const snap = await fetchJson(`${SNAPSHOT_BASE}/statewide-coarse.json`);
        if (!Array.isArray(snap.sections) || snap.sections.length === 0) {
          throw new LrsUnavailableError('snapshot missing sections');
        }
        sections = snap.sections;
        sourceInfo = { statewideSource: 'snapshot', snapshotDate: snap.generatedAt ?? null };
      } catch (_) {
        sections = await fetchStatewideSectionsLive(); // throws LrsUnavailableError if also down
        sourceInfo = { statewideSource: 'live', snapshotDate: null };
      }
      statewideCache = finalizeStatewideEntries(groupStatewideSections(sections));
      return statewideCache;
    },

    async searchLocalRoutes(term) {
      let raw;
      try {
        raw = await fetchJson(buildQueryUrl(LAYER_MILEPOINT, {
          where: andCurrent(buildLocalWhere(term)),
          outFields: 'RouteID,RouteType,RouteNumber,RouteDirection,CountyFIPSCode',
          returnGeometry: 'false',
          returnDistinctValues: 'true',
          resultRecordCount: String(LOCAL_SEARCH_LIMIT),
        }));
      } catch (err) {
        // Fallback: filter the snapshot's local index client-side (contains,
        // token-AND — matches the live WHERE-builder's multi-token support).
        // The parsed index (~28 MB decompressed) is cached across calls so a
        // debounced-keystroke outage doesn't re-fetch/re-parse on every call.
        if (!localIndexCache) {
          const snap = await fetchJson(`${SNAPSHOT_BASE}/local-index.json`);
          localIndexCache = snap.roads ?? [];
        }
        const tokens = String(term ?? '').toLowerCase().split(/\s+/).filter(Boolean);
        return localIndexCache
          .filter((r) => {
            const label = r.label.toLowerCase();
            return tokens.every((t) => label.includes(t));
          })
          .slice(0, LOCAL_SEARCH_LIMIT);
      }
      return buildLocalRouteEntries(raw).slice(0, LOCAL_SEARCH_LIMIT);
    },

    getRouteGeometry,

    async measureToPoint(key, m) {
      const { sections } = await getRouteGeometry(key);
      return interpolatePointAtMeasure(sections, m);
    },

    async pointToMeasure(key, point) {
      const { sections } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      return r ? r.m : null;
    },

    /** Nearest road + measure within 150 m of a clicked point. Fire-and-forget
     *  safe: resolves null on any failure, caches (incl. in-flight) per ~1 m. */
    locatePoint(point) {
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
        return Promise.resolve(null);
      }
      const cacheKey = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
      if (locateCache.has(cacheKey)) return locateCache.get(cacheKey);
      const promise = (async () => {
        const [statewideSections, localSections] = await Promise.all([
          querySectionsNear(LAYER_MILEPOST, point).catch(() => []),
          querySectionsNear(LAYER_MILEPOINT, point).catch(() => []),
        ]);
        const statewideHit = nearestOnSections(statewideSections, point, 'milepost');
        const localHit = nearestOnSections(localSections, point, 'milepoint');
        if (statewideHit && localHit) {
          return localHit.distanceMeters < statewideHit.distanceMeters ? localHit : statewideHit;
        }
        return statewideHit ?? localHit ?? null;
      })().catch(() => null);
      locateCache.set(cacheKey, promise);
      return promise;
    },

    /** Per-point LRS enrichment for a lane's points, computed locally against
     *  the cached geometry of the route key(s) the lane came from. Returns an
     *  array aligned 1:1 with `points`; each entry is
     *  { road, milepost, county, region } or null (no keys / out of range /
     *  geometry unavailable). Never throws — enrichment is best-effort. */
    async enrichPointsWithLrs(routeKeys, points) {
      const keys = [...new Set((routeKeys || []).filter(Boolean))];
      const pts = Array.isArray(points) ? points : [];
      if (!keys.length || !pts.length) return pts.map(() => null);

      const sectionSets = [];
      for (const key of keys) {
        try {
          const { sections } = await getRouteGeometry(key);
          const network = String(key).startsWith('milepoint') ? 'milepoint' : 'milepost';
          if (Array.isArray(sections) && sections.length) sectionSets.push({ sections, network });
        } catch (_) { /* skip a key whose geometry can't be loaded */ }
      }
      if (!sectionSets.length) return pts.map(() => null);

      return pts.map((pt) => {
        let best = null;
        for (const { sections, network } of sectionSets) {
          const hit = nearestOnSections(sections, pt, network);
          if (hit && (!best || hit.distanceMeters < best.distanceMeters)) best = hit;
        }
        if (!best) return null;
        return {
          road: best.routeLabel,
          milepost: best.measure,
          county: best.zone?.name ?? null,
          region: best.zone?.region ?? null,
        };
      });
    },
  };
}

export const aldotAdapter = createAldotAdapter();
